import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Grid2, 
  useMediaQuery, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Divider,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Button
} from '@mui/material';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { getEventBudgetCategories, getBudgetSplits } from '../actions/budget';
import { getEventTasks } from '../actions/tasks';
import AccessDenied from '../components/AccessDenied';
import { useAlert } from '../contexts/AlertContext';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';
import { 
  Assignment, 
  Category, 
  AccountBalance, 
  Person, 
  SplitscreenSharp,
  CheckCircle,
  Schedule,
  PauseCircle,
  Cancel,
  RateReview,
  Block,
  PlayArrow,
  FileDownload
} from '@mui/icons-material';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, ChartTooltip, Legend);

// Status color mapping
const getStatusColor = (status) => {
  const colors = {
    'To Do': '#3e95cd',
    'In Progress': '#ffcc00',
    'On Hold': '#ff5722',
    'Cancelled': '#f44336',
    'Pending Review': '#ff9800',
    'Scheduled': '#9c27b0',
    'Blocked': '#000000',
    'Done': '#76c043'
  };
  return colors[status] || '#757575';
};

// Status icon mapping
const getStatusIcon = (status) => {
  const icons = {
    'To Do': Assignment,
    'In Progress': PlayArrow,
    'On Hold': PauseCircle,
    'Cancelled': Cancel,
    'Pending Review': RateReview,
    'Scheduled': Schedule,
    'Blocked': Block,
    'Done': CheckCircle
  };
  const IconComponent = icons[status] || Assignment;
  return <IconComponent />;
};

// Capitalize name function
const capitalizeName = (name) => {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const Reports = () => {
  const { showAlert } = useAlert();
  const { reportType, eventId } = useParams(); // Capture eventId from URL params
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // State for fetched data
  const [expenses, setExpenses] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [splits, setSplits] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);

  // Fetch data based on report type and eventId
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (reportType === 'expenses') {
          const expensesData = await getEventBudgetCategories(eventId); // Pass eventId
          setExpenses(expensesData);
        } else if (reportType === 'tasks') {
          const tasksData = await getEventTasks(eventId); // Pass eventId
          setTasks(tasksData);
        } else if (reportType === 'splits') {
          const expensesData = await getEventBudgetCategories(eventId);
          setExpenses(expensesData);
          
          // Fetch splits for each expense
          const allSplits = await Promise.all(
            expensesData.map(async (expense) => {
              const expenseSplits = await getBudgetSplits(expense.id);
              return {
                expenseName: expense.name,
                paidBy: expense.paid_by,
                totalAmount: expense.expense,
                splits: expenseSplits
              };
            })
          );
          setSplits(allSplits);
        }
      } catch (error) {
        if (error?.response?.status === 403) {
          setAccessDenied(true);
        } else {
          showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Failed to load data', 'error');
        }
      }
    };
    if (eventId) fetchData(); // Fetch only if eventId is present
  }, [reportType, eventId]); // Trigger useEffect on reportType or eventId changes

  // Calculate who owes whom
  const calculateOwings = () => {
    const balances = {};

    splits.forEach(expenseSplit => {
      const { paidBy, splits: expenseSplits } = expenseSplit;
      
      // Initialize balances for paidBy if not exists
      if (!balances[paidBy]) {
        balances[paidBy] = {};
      }

      // Calculate what each person owes to paidBy
      expenseSplits.forEach(split => {
        const personName = split.person_name;
        if (personName !== paidBy) {
          if (!balances[paidBy][personName]) {
            balances[paidBy][personName] = 0;
          }
          balances[paidBy][personName] += parseFloat(split.split_amount);
        }
      });
    });

    // Simplify balances by netting out mutual debts
    const netBalances = [];
    Object.keys(balances).forEach(payer => {
      Object.keys(balances[payer] || {}).forEach(receiver => {
        const amountOwed = balances[payer][receiver] || 0;
        const reverseOwed = balances[receiver]?.[payer] || 0;
        
        if (amountOwed > reverseOwed) {
          netBalances.push({
            from: receiver,
            to: payer,
            amount: amountOwed - reverseOwed
          });
        }
      });
    });

    return netBalances;
  };

  // Group expenses by category and sum up the totals for each category
  const groupExpensesByCategory = () => {
    return expenses.reduce((grouped, item) => {
      const category = item.category_name || 'Other'; // Use 'Other' for null category names
      if (!grouped[category]) {
        grouped[category] = {
          total: 0,
          items: [],
        };
      }
      grouped[category].total += item.expense;
      grouped[category].items.push(item); // Push the individual expense items under that category
      return grouped;
    }, {});
  };

  // Group tasks by status
  const groupTasksByStatus = () => {
    return tasks.reduce((grouped, task) => {
      const status = task.status;
      if (!grouped[status]) {
        grouped[status] = [];
      }
      grouped[status].push(task);
      return grouped;
    }, {});
  };

  const groupedExpenses = groupExpensesByCategory();
  const groupedTasks = groupTasksByStatus();

  // Prepare Bar chart data for grouped expenses
  const barChartData = {
    labels: Object.keys(groupedExpenses), // Category names
    datasets: [
      {
        label: 'Expense',
        data: Object.values(groupedExpenses).map(group => group.total), // Total for each category
        backgroundColor: '#8884d8',
      },
    ],
  };

  // Prepare Doughnut chart data for tasks
  const countTasksByStatus = () => {
    const statusCounts = tasks.reduce((counts, task) => {
      counts[task.status] = (counts[task.status] || 0) + 1;
      return counts;
    }, {});
    return statusCounts;
  };

  const taskStatusCounts = countTasksByStatus();

  const chartData = {
    labels: ['To Do', 'In Progress', 'On Hold', 'Cancelled', 'Pending Review', 'Scheduled', 'Blocked', 'Done'],
    datasets: [
      {
        label: '# of Tasks',
        data: [
          taskStatusCounts['To Do'] || 0,
          taskStatusCounts['In Progress'] || 0,
          taskStatusCounts['On Hold'] || 0,
          taskStatusCounts['Cancelled'] || 0,
          taskStatusCounts['Pending Review'] || 0,
          taskStatusCounts['Scheduled'] || 0,
          taskStatusCounts['Blocked'] || 0,
          taskStatusCounts['Done'] || 0,
        ],
        backgroundColor: [
          '#3e95cd', '#ffcc00', '#ff5722', '#f44336', '#ff9800', '#9c27b0', '#000000', '#76c043'
        ],
        hoverOffset: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  const downloadCSV = (rows, filename) => {
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExpensesCSV = () => {
    const rows = [['Category', 'Expense Name', 'Amount']];
    Object.entries(groupedExpenses).forEach(([category, group]) => {
      group.items.forEach(item => {
        rows.push([category, item.name, item.expense.toFixed(2)]);
      });
    });
    downloadCSV(rows, 'expenses-report.csv');
  };

  const exportTasksCSV = () => {
    const rows = [['Title', 'Status', 'Assignee']];
    tasks.forEach(task => {
      rows.push([task.title, task.status, task.assignee || 'Unassigned']);
    });
    downloadCSV(rows, 'tasks-report.csv');
  };

  const exportSplitsCSV = () => {
    const rows = [];

    rows.push(['Net Balances']);
    rows.push(['From', 'To', 'Amount']);
    calculateOwings().forEach(b => {
      rows.push([capitalizeName(b.from), capitalizeName(b.to), b.amount.toFixed(2)]);
    });

    rows.push([]);

    rows.push(['Expense Details']);
    rows.push(['Expense Name', 'Paid By', 'Total Amount', 'Person', 'Split Amount']);
    splits.forEach(expenseSplit => {
      if (expenseSplit.splits.length === 0) {
        rows.push([expenseSplit.expenseName, capitalizeName(expenseSplit.paidBy), expenseSplit.totalAmount.toFixed(2), 'No splits', '']);
      } else {
        expenseSplit.splits.forEach(split => {
          rows.push([expenseSplit.expenseName, capitalizeName(expenseSplit.paidBy), expenseSplit.totalAmount.toFixed(2), capitalizeName(split.person_name), parseFloat(split.split_amount).toFixed(2)]);
        });
      }
    });

    downloadCSV(rows, 'splits-report.csv');
  };

  if (accessDenied) return <AccessDenied />;

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
      p: { xs: 0, sm: 0, md: 4 },
      mt: { xs: 5, md: 0 }
    }}>

      {reportType === 'expenses' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="outlined" startIcon={<FileDownload />} onClick={exportExpensesCSV} size="small">
              Download
            </Button>
          </Box>
          {/* Chart Section */}
          <Card sx={{
            mb: 4,
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)'
              : '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.8)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.3)',
            width: isMobile ? '100%' : '50%'
          }}>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}10)`,
                  mr: 2
                }}>
                  <Category sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Expenses by Category
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Detailed breakdown of spending across categories
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mb: 4 }}>
                <Bar 
                  data={{
                    ...barChartData,
                    datasets: [{
                      ...barChartData.datasets[0],
                      backgroundColor: 'rgba(136, 132, 216, 0.8)',
                      borderColor: 'rgba(136, 132, 216, 1)',
                      borderWidth: 2,
                      borderRadius: 8,
                      borderSkipped: false,
                    }]
                  }} 
                  options={{
                    ...chartOptions,
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                        titleColor: theme.palette.text.primary,
                        bodyColor: theme.palette.text.primary,
                        borderColor: theme.palette.divider,
                        borderWidth: 1,
                        cornerRadius: 8,
                        padding: 12
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                        },
                        ticks: {
                          color: theme.palette.text.secondary,
                          font: {
                            weight: '600',
                            size: isMobile ? 10 : 14
                          }
                        }
                      },
                      x: {
                        grid: {
                          display: false
                        },
                        ticks: {
                          color: theme.palette.text.secondary,
                          font: {
                            weight: '600',
                            size: isMobile ? 8 : 14
                          }
                        }
                      }
                    },
                    responsive: true,
                    maintainAspectRatio: true,
                  }} 
                />
              </Box>

              {/* Total Tasks Summary - Right Side */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 4, 
                borderRadius: 3,
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.03) 100%)',
                border: `2px solid ${theme.palette.success.main}20`,
                boxShadow: `0 4px 20px ${theme.palette.success.main}10`,
                flexDirection: 'column',
                textAlign: 'center'
              }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, color: theme.palette.success.main, mb: 1 }}>
                  ${Object.values(groupedExpenses).reduce((total, group) => total + group.total, 0).toFixed(2)}
                </Typography>
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Total Expenses
                </Typography>
                <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary" sx={{ mt: 0.5 }}>
                  Across all categories
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Category Cards */}
          <Box sx={cardGridSx(280, 3)}>
            {Object.entries(groupedExpenses).map(([category, group]) => (
              <Box key={category} sx={cardItemSx(420)}>
                <Card sx={{
                  height: '100%',
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                      : '0 8px 30px rgba(0, 0, 0, 0.15)',
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        {category || 'Other'}
                      </Typography>
                      <Chip 
                        label={`$${group.total.toFixed(2)}`}
                        color="primary"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={1}>
                      {group.items.map(item => (
                        <Box 
                          key={item.id}
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            p: 1,
                            borderRadius: 1,
                            background: theme.palette.mode === 'dark' 
                              ? '#2a2a2a'
                              : 'rgba(0, 0, 0, 0.02)',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {item.name}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            ${item.expense.toFixed(2)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {reportType === 'tasks' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="outlined" startIcon={<FileDownload />} onClick={exportTasksCSV} size="small">
              Download
            </Button>
          </Box>
          {/* Chart and Summary Section */}
          <Card sx={{
            mb: 4,
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)'
              : '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.8)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.3)',
          }}>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}10)`,
                  mr: 2
                }}>
                  <Assignment sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Tasks Overview
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Visual breakdown of task distribution by status
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 4, 
                borderRadius: 3,
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 150, 243, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)',
                border: `2px solid ${theme.palette.primary.main}20`,
                boxShadow: `0 4px 20px ${theme.palette.primary.main}10`,
                flexDirection: 'column',
                textAlign: 'center',
                marginBottom: 4
              }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}>
                  {tasks.length}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Total Tasks
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Across all status categories
                </Typography>
              </Box>
              
              {tasks.length > 0 ? (
                <>
                  {/* Chart and Summary Layout */}
                  <Grid2 container spacing={4} alignItems="center">
                    {/* Pie Chart - Left Side */}
                    <Grid2 size={{ xs: 12, md: 3 }}>
                      <Box sx={{ position: 'relative' }}>
                        <Doughnut 
                          data={{
                            ...chartData,
                            datasets: [{
                              ...chartData.datasets[0],
                              backgroundColor: [
                                'rgba(62, 149, 205, 0.8)',
                                'rgba(255, 204, 0, 0.8)', 
                                'rgba(255, 87, 34, 0.8)',
                                'rgba(244, 67, 54, 0.8)',
                                'rgba(255, 152, 0, 0.8)',
                                'rgba(156, 39, 176, 0.8)',
                                'rgba(0, 0, 0, 0.8)',
                                'rgba(118, 192, 67, 0.8)'
                              ],
                              borderWidth: 3,
                              borderColor: theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
                              hoverBorderWidth: 4,
                              hoverOffset: 8,
                            }]
                          }} 
                          options={{
                            plugins: {
                              legend: {
                                position: 'bottom',
                                labels: {
                                  padding: 15,
                                  font: {
                                    size: 11,
                                    weight: '600'
                                  },
                                  color: theme.palette.text.primary,
                                  usePointStyle: true,
                                  pointStyle: 'circle'
                                }
                              },
                              tooltip: {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                                titleColor: theme.palette.text.primary,
                                bodyColor: theme.palette.text.primary,
                                borderColor: theme.palette.divider,
                                borderWidth: 1,
                                cornerRadius: 8,
                                padding: 12
                              }
                            },
                            cutout: '60%',
                            responsive: true,
                            maintainAspectRatio: true,
                          }} 
                        />
                      </Box>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 9 }}>
                      {/* Status Cards Section */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: theme.palette.text.primary }}>
                          Tasks by Status
                        </Typography>
                        <Box sx={cardGridSx(280, 3)}>
                          {Object.entries(groupedTasks).map(([status, statusTasks]) => (
                            <Box key={status} sx={cardItemSx(420)}>
                              <Card sx={{
                                height: '100%',
                                background: theme.palette.mode === 'dark' 
                                  ? '#1e1e1e'
                                  : 'rgba(255, 255, 255, 0.9)',
                                borderRadius: 3,
                                boxShadow: theme.palette.mode === 'dark'
                                  ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                                  : '0 4px 20px rgba(0, 0, 0, 0.1)',
                                border: theme.palette.mode === 'dark'
                                  ? '1px solid rgba(255, 255, 255, 0.1)'
                                  : '1px solid rgba(255, 255, 255, 0.2)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: theme.palette.mode === 'dark'
                                    ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                                    : '0 8px 30px rgba(0, 0, 0, 0.15)',
                                }
                              }}>
                                <CardContent sx={{ 
                                  p: 3,
                                  background: theme.palette.mode === 'dark' 
                                    ? '#1e1e1e'
                                    : 'rgba(255, 255, 255, 0.9)',
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Box sx={{
                                        p: 1,
                                        borderRadius: 1,
                                        background: `${getStatusColor(status)}20`,
                                        mr: 1.5
                                      }}>
                                        {getStatusIcon(status)}
                                      </Box>
                                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                        {status}
                                      </Typography>
                                    </Box>
                                    <Chip 
                                      label={statusTasks.length}
                                      sx={{ 
                                        backgroundColor: getStatusColor(status),
                                        color: 'white',
                                        fontWeight: 600
                                      }}
                                    />
                                  </Box>
                                  <Divider sx={{ mb: 2 }} />
                                  <Stack spacing={1}>
                                    {statusTasks.map(task => (
                                      <Box 
                                        key={task.id}
                                        sx={{ 
                                          display: 'flex', 
                                          justifyContent: 'space-between', 
                                          alignItems: 'center',
                                          p: 1,
                                          borderRadius: 1,
                                          background: theme.palette.mode === 'dark' 
                                            ? '#2a2a2a'
                                            : 'rgba(0, 0, 0, 0.02)',
                                        }}
                                      >
                                        <Typography variant="body2" color="text.secondary">
                                          {task.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {capitalizeName(task.assignee) || 'Unassigned'}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                </CardContent>
                              </Card>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Grid2>
                  </Grid2>
                </>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 6, 
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 193, 7, 0.1)'
                    : 'rgba(255, 193, 7, 0.05)',
                  border: `2px solid ${theme.palette.warning.main}30`,
                  textAlign: 'center'
                }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.warning.main, mb: 1 }}>
                      No Tasks Available
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      There are no tasks to display in this report.
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {reportType === 'splits' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="outlined" startIcon={<FileDownload />} onClick={exportSplitsCSV} size="small">
              Download
            </Button>
          </Box>
          {/* Summary Card */}
          <Card sx={{
            mb: 4,
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)'
              : '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.8)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.3)',
          }}>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main}20, ${theme.palette.secondary.main}10)`,
                  mr: 2
                }}>
                  <SplitscreenSharp sx={{ color: theme.palette.secondary.main, fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Expense Splits Overview
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Track who owes what to whom
                  </Typography>
                </Box>
              </Box>

              {expenses.length > 0 ? (
                <>
                  {/* Who Owes Whom Section */}
                  <Card sx={{
                    background: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 3,
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                      : '0 4px 20px rgba(0, 0, 0, 0.1)',
                    border: theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.1)'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Person sx={{ mr: 2, color: theme.palette.primary.main }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Split Expenses by Person
                        </Typography>
                      </Box>
                      {(() => {
                        const balances = calculateOwings();
                        return balances.length > 0 ? (
                          <Box sx={cardGridSx(240, 3)}>
                            {balances.map((balance, index) => (
                              <Box key={index} sx={cardItemSx(300)}>
                                <Card sx={{
                                  background: theme.palette.mode === 'dark' 
                                    ? 'rgba(244, 67, 54, 0.1)'
                                    : 'rgba(244, 67, 54, 0.05)',
                                  borderRadius: 2,
                                  boxShadow: theme.palette.mode === 'dark'
                                    ? '0 2px 10px rgba(0, 0, 0, 0.1)'
                                    : '0 2px 10px rgba(0, 0, 0, 0.05)',
                                  border: `1px solid ${theme.palette.error.main}20`,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: theme.palette.mode === 'dark'
                                      ? '0 4px 15px rgba(0, 0, 0, 0.2)'
                                      : '0 4px 15px rgba(0, 0, 0, 0.1)',
                                  }
                                }}>
                                  <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                                    {/* Avatars */}
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center',
                                      mb: 2,
                                      gap: 1
                                    }}>
                                      <Avatar sx={{ 
                                        bgcolor: theme.palette.error.main, 
                                        width: 28, 
                                        height: 28,
                                        fontSize: '0.8rem',
                                        fontWeight: 600
                                      }}>
                                        {balance.from.charAt(0).toUpperCase()}
                                      </Avatar>
                                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        owes
                                      </Typography>
                                      <Avatar sx={{ 
                                        bgcolor: theme.palette.success.main, 
                                        width: 28, 
                                        height: 28,
                                        fontSize: '0.8rem',
                                        fontWeight: 600
                                      }}>
                                        {balance.to.charAt(0).toUpperCase()}
                                      </Avatar>
                                    </Box>
                                    
                                    {/* Amount */}
                                    <Typography variant="h6" sx={{ 
                                      fontWeight: 700, 
                                      color: theme.palette.error.main,
                                      mb: 1
                                    }}>
                                      ${balance.amount.toFixed(2)}
                                    </Typography>
                                    
                                    {/* Names */}
                                    <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>
                                      {capitalizeName(balance.from)} → {capitalizeName(balance.to)}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            p: 4,
                            borderRadius: 2,
                            background: theme.palette.mode === 'dark' 
                              ? 'rgba(76, 175, 80, 0.1)'
                              : 'rgba(76, 175, 80, 0.05)',
                            border: `1px solid ${theme.palette.success.main}30`
                          }}>
                            <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                              All expenses are settled! No outstanding balances.
                            </Typography>
                          </Box>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  {/* Expense Split Cards */}
                  <Card sx={{
                    background: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 3,
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                      : '0 4px 20px rgba(0, 0, 0, 0.1)',
                    border: theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.1)'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    mt: 3,
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <SplitscreenSharp sx={{ mr: 2, color: theme.palette.secondary.main }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Expense Details
                        </Typography>
                      </Box>
                      <Box sx={cardGridSx(280, 3)}>
                        {splits.map((expenseSplit, index) => (
                          <Box key={index} sx={cardItemSx(420)}>
                            <Card sx={{
                              background: theme.palette.mode === 'dark' 
                                ? '#1e1e1e'
                                : 'rgba(255, 255, 255, 0.7)',
                              borderRadius: 2,
                              boxShadow: theme.palette.mode === 'dark'
                                ? '0 2px 10px rgba(0, 0, 0, 0.1)'
                                : '0 2px 10px rgba(0, 0, 0, 0.05)',
                              border: theme.palette.mode === 'dark'
                                ? '1px solid rgba(255, 255, 255, 0.05)'
                                : '1px solid rgba(255, 255, 255, 0.3)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: theme.palette.mode === 'dark'
                                  ? '0 4px 15px rgba(0, 0, 0, 0.2)'
                                  : '0 4px 15px rgba(0, 0, 0, 0.1)',
                              }
                            }}>
                              <CardContent sx={{ 
                                p: 2.5,
                                background: theme.palette.mode === 'dark' 
                                  ? '#1e1e1e'
                                  : 'rgba(255, 255, 255, 0.7)',
                              }}>
                                {/* Expense Header */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {expenseSplit.expenseName}
                                  </Typography>
                                  <Chip 
                                    label={`$${expenseSplit.totalAmount.toFixed(2)}`}
                                    color="secondary"
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </Box>

                                {/* Paid By */}
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mb: 2,
                                  p: 1,
                                  borderRadius: 1,
                                  background: theme.palette.mode === 'dark' 
                                    ? 'rgba(76, 175, 80, 0.1)'
                                    : 'rgba(76, 175, 80, 0.05)',
                                  border: `1px solid ${theme.palette.success.main}20`
                                }}>
                                  <Avatar sx={{ 
                                    bgcolor: theme.palette.success.main, 
                                    width: 24, 
                                    height: 24, 
                                    mr: 1.5,
                                    fontSize: '0.75rem'
                                  }}>
                                    {expenseSplit.paidBy.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Paid by
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {capitalizeName(expenseSplit.paidBy)}
                                    </Typography>
                                  </Box>
                                </Box>

                                <Divider sx={{ mb: 1.5 }} />

                                {/* Split Details */}
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                  Split Details
                                </Typography>
                                {expenseSplit.splits.length === 0 ? (
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    p: 2,
                                    borderRadius: 1,
                                    background: theme.palette.mode === 'dark' 
                                      ? 'rgba(255, 193, 7, 0.1)'
                                      : 'rgba(255, 193, 7, 0.05)',
                                    border: `1px solid ${theme.palette.warning.main}30`
                                  }}>
                                    <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                      No Splits for this expense
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Stack spacing={0.5}>
                                    {expenseSplit.splits.map((split, splitIndex) => (
                                      <Box 
                                        key={splitIndex}
                                        sx={{ 
                                          display: 'flex', 
                                          justifyContent: 'space-between', 
                                          alignItems: 'center',
                                          p: 0.8,
                                          borderRadius: 0.5,
                                          background: theme.palette.mode === 'dark' 
                                            ? '#2a2a2a'
                                            : 'rgba(0, 0, 0, 0.02)',
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Avatar sx={{ 
                                            bgcolor: theme.palette.primary.main, 
                                            width: 20, 
                                            height: 20, 
                                            mr: 1,
                                            fontSize: '0.65rem'
                                          }}>
                                            {split.person_name.charAt(0).toUpperCase()}
                                          </Avatar>
                                          <Typography variant="caption">
                                            {capitalizeName(split.person_name)}
                                          </Typography>
                                        </Box>
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                          ${parseFloat(split.split_amount).toFixed(2)}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                )}
                              </CardContent>
                            </Card>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 6, 
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 193, 7, 0.1)'
                    : 'rgba(255, 193, 7, 0.05)',
                  border: `2px solid ${theme.palette.warning.main}30`,
                  textAlign: 'center'
                }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.warning.main, mb: 1 }}>
                      No Expenses Available
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      There are no expenses to display in this report.
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default Reports;